import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import dayjs from 'dayjs';
import { createProjectBudget, deleteProjectBudget, getProjectBudgets } from '../../../Redux/actions';
import { THEME_COLOR, buttonStyles } from '../../../theme/formStyles';
import BudgetSheetDialog from './BudgetSheetDialog';
import { inr } from './budget.utils';

// "Project budget" card in the project editor (admins): the budget sheet versions, newest first
export default function BudgetVersionList({ projectId, facts }) {
    const dispatch = useDispatch();
    const [budgets, setBudgets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [openId, setOpenId] = useState(null);

    const load = useCallback(() => {
        dispatch(getProjectBudgets(projectId)).then((res) => {
            setLoading(false);
            if (res?.data?.success) { setBudgets(res.data.data || []); setError(null); }
            else setError(res?.data?.message || 'Could not load budget sheets');
        });
    }, [dispatch, projectId]);
    useEffect(() => { load(); }, [load]);

    const create = () => {
        setBusy(true);
        dispatch(createProjectBudget(projectId)).then((res) => {
            setBusy(false);
            if (res?.data?.success) { load(); setOpenId(res.data.data.id); }
            else setError(res?.data?.message || 'Could not add a version');
        });
    };
    const remove = (b) => {
        if (!window.confirm(`Delete ${b.name}? Its PDF download history goes with it.`)) return;
        dispatch(deleteProjectBudget(projectId, b.id)).then((res) => {
            if (res?.data?.success) load(); else setError(res?.data?.message || 'Could not delete');
        });
    };

    return (
        <Box sx={{ border: '1px solid #e5e9ee', borderRadius: 3, p: 1.75, bgcolor: '#fff' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '13.5px' }}>💰 Budget sheets</Typography>
            <Typography sx={{ fontSize: '11.5px', color: '#6b7785', mb: 1 }}>Admins only. A new version starts as a copy of the latest.</Typography>
            {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
            {loading ? <CircularProgress size={20} sx={{ color: THEME_COLOR }} /> : budgets.map((b, i) => (
                <Box key={b.id} onClick={() => setOpenId(b.id)} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderTop: i ? '1px solid #eef1f4' : 'none', cursor: 'pointer', '&:hover': { bgcolor: '#fafbfc' } }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: '12.5px', fontWeight: 600 }}>{b.name}{i === 0 && <span style={{ color: THEME_COLOR, fontWeight: 500 }}> · latest</span>}</Typography>
                        <Typography noWrap sx={{ fontSize: '11px', color: '#6b7785' }}>
                            {inr(b.profit)} profit{b.margin !== null ? ` · ${b.margin}%` : ''} · {dayjs(b.updated_at).format('D MMM')}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={e => { e.stopPropagation(); remove(b); }} aria-label="Delete version"><DeleteOutlineIcon sx={{ fontSize: 17 }} /></IconButton>
                </Box>
            ))}
            <Button fullWidth size="small" variant="outlined" onClick={create} disabled={busy} sx={{ ...buttonStyles.outlined, mt: 1 }}>
                {busy ? <CircularProgress size={16} /> : budgets.length ? `＋ Version ${budgets[0].version + 1}` : '＋ Start budget sheet'}
            </Button>
            {openId && <BudgetSheetDialog projectId={projectId} budgetId={openId} facts={facts} onClose={() => { setOpenId(null); load(); }} onSaved={load} />}
        </Box>
    );
}
